import type { FigmaIconDefinition, FigmaIconGroup, FigmaIconGroupId, FigmaIconSizeVariation } from './types';

export type {
  FigmaIconDefinition,
  FigmaIconGroup,
  FigmaIconGroupId,
  FigmaIconImplementationStatus,
  FigmaIconSizeVariation,
} from './types';

/**
 * Icon group source node IDs reference the 2026 Kai Styleguide Figma file
 * (the "Icons" section, node 19:296). Admin-nav is not yet re-extracted from
 * the new file — its node ID will update when that group is selected.
 */
export const figmaIconGroups: readonly FigmaIconGroup[] = [
  { id: 'admin-nav', label: 'Web Main Navigation', sourceNodeId: '278:4079' },
  { id: 'standard-ui', label: 'Standard UI', sourceNodeId: '19:477' },
  { id: 'tasks', label: 'Tasks', sourceNodeId: '19:340' },
  { id: 'modifiers', label: 'Modifiers', sourceNodeId: '19:717' },
  { id: 'extra-needs', label: 'Extra Needs', sourceNodeId: '579:22' },
  { id: 'artifacts', label: 'Artifacts', sourceNodeId: '19:301' },
] as const;

export const figmaIcons: readonly FigmaIconDefinition[] = [
  // ── admin-nav (not yet re-extracted from the new Figma file) ──────────────
  { exportName: 'DashboardIcon', figmaLabel: 'Dashboard', groupId: 'admin-nav', implementationStatus: 'implemented', runtimeExportName: 'DashboardIcon' },
  {
    exportName: 'PropertiesIcon',
    figmaLabel: 'Properties',
    groupId: 'admin-nav',
    implementationStatus: 'implemented',
    runtimeExportName: 'PropertiesIcon',
    notes: 'Canonical semantic name now exports directly while preserving HomesIcon for backward compatibility.',
  },
  { exportName: 'MarketsIcon', figmaLabel: 'Markets', groupId: 'admin-nav', implementationStatus: 'implemented', runtimeExportName: 'MarketsIcon' },
  { exportName: 'ContractorsIcon', figmaLabel: 'Contractors', groupId: 'admin-nav', implementationStatus: 'implemented', runtimeExportName: 'ContractorsIcon' },
  { exportName: 'ProductsIcon', figmaLabel: 'Products', groupId: 'admin-nav', implementationStatus: 'implemented', runtimeExportName: 'ProductsIcon' },
  { exportName: 'TemplatesIcon', figmaLabel: 'Templates', groupId: 'admin-nav', implementationStatus: 'implemented', runtimeExportName: 'TemplatesIcon' },
  { exportName: 'UsersIcon', figmaLabel: 'Users', groupId: 'admin-nav', implementationStatus: 'implemented', runtimeExportName: 'UsersIcon' },
  {
    exportName: 'OrgSettingsIcon',
    figmaLabel: 'Settings',
    groupId: 'admin-nav',
    implementationStatus: 'implemented',
    runtimeExportName: 'OrgSettingsIcon',
    notes: 'Nav-specific settings glyph to avoid collision with the generic utility SettingsIcon.',
  },

  // ── standard-ui ───────────────────────────────────────────────────────────
  { exportName: 'FilterIcon', figmaLabel: 'Filter', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'FilterIcon' },
  { exportName: 'SortIcon', figmaLabel: 'Sort', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'SortIcon' },
  { exportName: 'SearchIcon', figmaLabel: 'Search', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'SearchIcon' },
  { exportName: 'NoteIcon', figmaLabel: 'Note', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'NoteIcon' },
  {
    exportName: 'DropdownArrowIcon',
    figmaLabel: 'Dropdown arrow',
    groupId: 'standard-ui',
    implementationStatus: 'implemented',
    runtimeExportName: 'DropdownArrowIcon',
  },
  { exportName: 'PlusIcon', figmaLabel: 'Plus', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'PlusIcon' },
  { exportName: 'MinusIcon', figmaLabel: 'Minus', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'MinusIcon' },
  { exportName: 'CheckIcon', figmaLabel: 'Check mark', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'CheckIcon' },
  { exportName: 'CloseIcon', figmaLabel: 'Close', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'CloseIcon' },
  { exportName: 'TrashIcon', figmaLabel: 'Trash can', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'TrashIcon' },
  { exportName: 'InfoCircleIcon', figmaLabel: 'Info circle', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'InfoCircleIcon' },
  { exportName: 'ErrorIcon', figmaLabel: 'Error', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'ErrorIcon' },
  { exportName: 'AlertIcon', figmaLabel: 'Alert', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'AlertIcon' },
  { exportName: 'FlagIcon', figmaLabel: 'Flag', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'FlagIcon' },
  {
    exportName: 'NotificationIcon',
    figmaLabel: 'Notification',
    groupId: 'standard-ui',
    implementationStatus: 'implemented',
    runtimeExportName: 'NotificationIcon',
  },
  { exportName: 'CameraIcon', figmaLabel: 'Camera', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'CameraIcon' },
  { exportName: 'SupportIcon', figmaLabel: 'Support', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'SupportIcon' },
  { exportName: 'DownloadIcon', figmaLabel: 'Download', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'DownloadIcon' },
  { exportName: 'UploadIcon', figmaLabel: 'Upload', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'UploadIcon' },
  {
    exportName: 'UploadFromComputerIcon',
    figmaLabel: 'Upload from Computer',
    groupId: 'standard-ui',
    implementationStatus: 'implemented',
    runtimeExportName: 'UploadFromComputerIcon',
  },
  { exportName: 'UndoIcon', figmaLabel: 'Undo', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'UndoIcon' },
  {
    exportName: 'MoreVerticalIcon',
    figmaLabel: 'Vertical Ellipsis',
    groupId: 'standard-ui',
    implementationStatus: 'implemented',
    runtimeExportName: 'MoreVerticalIcon',
  },
  { exportName: 'LogoutIcon', figmaLabel: 'Logout', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'LogoutIcon' },
  { exportName: 'CalendarIcon', figmaLabel: 'Calendar', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'CalendarIcon' },
  { exportName: 'EditIcon', figmaLabel: 'Edit', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'EditIcon' },
  { exportName: 'BackToTopIcon', figmaLabel: 'Back to Top', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'BackToTopIcon' },
  // Arrow set — the new Figma has dedicated directional arrows.
  { exportName: 'BackArrowIcon', figmaLabel: 'Back arrow', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'BackArrowIcon' },
  { exportName: 'UpArrowIcon', figmaLabel: 'Up Arrow', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'UpArrowIcon' },
  { exportName: 'DownArrowIcon', figmaLabel: 'Down Arrow', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'DownArrowIcon' },
  { exportName: 'LeftArrowIcon', figmaLabel: 'Left arrow', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'LeftArrowIcon' },
  { exportName: 'RightArrowIcon', figmaLabel: 'Right Arrow', groupId: 'standard-ui', implementationStatus: 'implemented', runtimeExportName: 'RightArrowIcon' },
  { exportName: 'NextArrowIcon', figmaLabel: 'Next arrow', groupId: 'standard-ui', implementationStatus: 'legacy-alias', runtimeExportName: 'RightIcon' },
  {
    exportName: 'PreviousArrowIcon',
    figmaLabel: 'Previous arrow',
    groupId: 'standard-ui',
    implementationStatus: 'legacy-alias',
    runtimeExportName: 'LeftIcon',
  },
  // Legacy icons not in the new Figma selection (kept for backward compat).
  { exportName: 'SettingsIcon', figmaLabel: 'Settings', groupId: 'standard-ui', implementationStatus: 'legacy-alias', runtimeExportName: 'SettingsIcon' },
  {
    exportName: 'UserProfileIcon',
    figmaLabel: 'User Profile',
    groupId: 'standard-ui',
    implementationStatus: 'legacy-alias',
    runtimeExportName: 'UserProfileIcon',
  },
  {
    exportName: 'CollapseCaretIcon',
    figmaLabel: 'Collapse Carat',
    groupId: 'standard-ui',
    implementationStatus: 'legacy-alias',
    runtimeExportName: 'CollapseCaretIcon',
  },
  { exportName: 'CloudSyncIcon', figmaLabel: 'Cloud sync', groupId: 'standard-ui', implementationStatus: 'legacy-alias', runtimeExportName: 'CloudSyncIcon' },

  // ── tasks ─────────────────────────────────────────────────────────────────
  { exportName: 'NotStartedIcon', figmaLabel: 'Not started', groupId: 'tasks', implementationStatus: 'implemented', runtimeExportName: 'NotStartedIcon' },
  { exportName: 'InProgressIcon', figmaLabel: 'In progress', groupId: 'tasks', implementationStatus: 'implemented', runtimeExportName: 'InProgressIcon' },
  { exportName: 'ReworkIcon', figmaLabel: 'Rework', groupId: 'tasks', implementationStatus: 'implemented', runtimeExportName: 'ReworkIcon' },
  { exportName: 'CompleteIcon', figmaLabel: 'Complete', groupId: 'tasks', implementationStatus: 'implemented', runtimeExportName: 'CompleteIcon' },

  // ── modifiers (NEW group — 2026 Kai Styleguide) ───────────────────────────
  {
    exportName: 'TenantResponsibleIcon',
    figmaLabel: 'Tenant Responsible',
    groupId: 'modifiers',
    implementationStatus: 'implemented',
    runtimeExportName: 'TenantResponsibleIcon',
  },
  {
    exportName: 'UnassignedContractorIcon',
    figmaLabel: 'Unassigned Contractor',
    groupId: 'modifiers',
    implementationStatus: 'implemented',
    runtimeExportName: 'UnassignedContractorIcon',
  },
  {
    exportName: 'OutOfStockItemIcon',
    figmaLabel: 'Out of stock item',
    groupId: 'modifiers',
    implementationStatus: 'implemented',
    runtimeExportName: 'OutOfStockItemIcon',
  },
  { exportName: 'DeferredIcon', figmaLabel: 'Deferred', groupId: 'modifiers', implementationStatus: 'implemented', runtimeExportName: 'DeferredIcon' },

  // ── extra-needs ───────────────────────────────────────────────────────────
  { exportName: 'PauseIcon', figmaLabel: 'Pause', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'PauseIcon' },
  { exportName: 'StopIcon', figmaLabel: 'Stop', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'StopIcon' },
  { exportName: 'LockIcon', figmaLabel: 'Lock', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'LockIcon' },
  { exportName: 'DirectionsIcon', figmaLabel: 'Directions', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'DirectionsIcon' },
  { exportName: 'PhotoIcon', figmaLabel: 'Photo', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'PhotoIcon' },
  { exportName: 'VideoIcon', figmaLabel: 'Video', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'VideoIcon' },
  {
    exportName: 'InsightTipIcon',
    figmaLabel: 'Insight / tip',
    groupId: 'extra-needs',
    implementationStatus: 'implemented',
    runtimeExportName: 'InsightTipIcon',
  },
  { exportName: 'OnlineIcon', figmaLabel: 'Online', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'OnlineIcon' },
  { exportName: 'OfflineIcon', figmaLabel: 'Offline', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'OfflineIcon' },
  {
    exportName: 'ImageUnavailableIcon',
    figmaLabel: 'Image Unavailable',
    groupId: 'extra-needs',
    implementationStatus: 'implemented',
    runtimeExportName: 'ImageUnavailableIcon',
  },
  {
    exportName: 'BatterySaveModeIcon',
    figmaLabel: 'Battery Save Mode',
    groupId: 'extra-needs',
    implementationStatus: 'implemented',
    runtimeExportName: 'BatterySaveModeIcon',
  },
  { exportName: 'ArchiveIcon', figmaLabel: 'Archive', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'ArchiveIcon' },
  { exportName: 'DuplicateIcon', figmaLabel: 'Duplicate', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'DuplicateIcon' },
  { exportName: 'ProjectsIcon', figmaLabel: 'Projects', groupId: 'extra-needs', implementationStatus: 'implemented', runtimeExportName: 'ProjectsIcon' },
  {
    exportName: 'HexagonOutlineIcon',
    figmaLabel: 'Progress Stepper (Outlined)',
    groupId: 'extra-needs',
    implementationStatus: 'implemented',
    runtimeExportName: 'HexagonOutlineIcon',
  },
  {
    exportName: 'HexagonFilledIcon',
    figmaLabel: 'Progress Stepper (Filled)',
    groupId: 'extra-needs',
    implementationStatus: 'implemented',
    runtimeExportName: 'HexagonFilledIcon',
  },
  // Legacy icons not in the new Figma selection (kept for backward compat).
  {
    exportName: 'MissingPhotoIcon',
    figmaLabel: 'Missing Photo',
    groupId: 'extra-needs',
    implementationStatus: 'legacy-alias',
    runtimeExportName: 'MissingPhotoIcon',
  },
  { exportName: 'GroupIcon', figmaLabel: 'Group', groupId: 'extra-needs', implementationStatus: 'legacy-alias', runtimeExportName: 'GroupIcon' },
  { exportName: 'TaskIcon', figmaLabel: 'Task', groupId: 'extra-needs', implementationStatus: 'legacy-alias', runtimeExportName: 'TasksIcon' },
  { exportName: 'TradesIcon', figmaLabel: 'Trades', groupId: 'extra-needs', implementationStatus: 'legacy-alias', runtimeExportName: 'TradesIcon' },
  { exportName: 'StorageIcon', figmaLabel: 'Storage', groupId: 'extra-needs', implementationStatus: 'legacy-alias', runtimeExportName: 'StorageIcon' },
  { exportName: 'KaiIcon', figmaLabel: 'Kai Wordmark', groupId: 'extra-needs', implementationStatus: 'legacy-alias', runtimeExportName: 'KaiIcon' },

  // ── artifacts ─────────────────────────────────────────────────────────────
  { exportName: 'ScopeIcon', figmaLabel: 'Scope', groupId: 'artifacts', implementationStatus: 'implemented', runtimeExportName: 'ScopeIcon' },
  {
    exportName: 'ChangeOrderIcon',
    figmaLabel: 'Change order',
    groupId: 'artifacts',
    implementationStatus: 'implemented',
    runtimeExportName: 'ChangeOrderIcon',
  },
  { exportName: 'CloseOutIcon', figmaLabel: 'Close Out', groupId: 'artifacts', implementationStatus: 'implemented', runtimeExportName: 'CloseOutIcon' },
  {
    exportName: 'ScopeAddendumIcon',
    figmaLabel: 'Scope (Addendum)',
    groupId: 'artifacts',
    implementationStatus: 'implemented',
    runtimeExportName: 'ScopeAddendumIcon',
  },
  { exportName: 'PunchListIcon', figmaLabel: 'Punch List', groupId: 'artifacts', implementationStatus: 'implemented', runtimeExportName: 'PunchListIcon' },
] as const;

export const figmaIconCatalogSource = {
  colorsSource: 'Figma facelift palette extraction, 2026-05-05 (material-theme 691:123)',
  iconsSource: '2026 Kai Styleguide icon extraction, 2026-07-02 (Icons section 19:296; groups 19:477, 19:340, 19:717, 579:22, 19:301)',
  sizeVariationsSource: 'Figma icon size variations extraction, 2026-05-05 (size variations 774:526)',
  sizeVariationsReviewNote: 'Compatibility iconSizes remain public for now pending design review.',
} as const;

export const figmaIconGroupMap = Object.fromEntries(
  figmaIconGroups.map(group => [group.id, group]),
) as Record<FigmaIconGroupId, FigmaIconGroup>;

export const figmaIconsByGroup: Record<FigmaIconGroupId, readonly FigmaIconDefinition[]> = {
  'admin-nav': figmaIcons.filter(icon => icon.groupId === 'admin-nav'),
  'standard-ui': figmaIcons.filter(icon => icon.groupId === 'standard-ui'),
  tasks: figmaIcons.filter(icon => icon.groupId === 'tasks'),
  modifiers: figmaIcons.filter(icon => icon.groupId === 'modifiers'),
  'extra-needs': figmaIcons.filter(icon => icon.groupId === 'extra-needs'),
  artifacts: figmaIcons.filter(icon => icon.groupId === 'artifacts'),
};

export const figmaIconSizes: readonly FigmaIconSizeVariation[] = [16, 24, 32, 48, 64];
export const figmaDefaultIconSize: FigmaIconSizeVariation = 24;
